/**
 * ENDPOINTS DO BACKEND - Documentação Centralizada
 * Baseado na lista oficial fornecida
 */

export const API_ENDPOINTS = {
  // 🏢 Organizations
  ORGANIZATIONS: {
    LIST: '/organizations/',                           // GET - Master only
    MY: '/organizations/my',                           // GET - Minha organização
    USAGE: (id: number) => `/organizations/${id}/usage`, // GET - Estatísticas
    LIMITS_PROPOSALS: (id: number) => `/organizations/${id}/limits/proposals`, // GET - Limite de propostas
  },

  // 📝 Proposals
  PROPOSALS: {
    LIST: '/proposals/',                               // GET - Minhas propostas (com paginação)
    CREATE: '/proposals/',                             // POST - Criar nova proposta
    DETAILS: (id: number | string) => `/proposals/${id}`, // GET - Detalhes completos
    UPDATE: (id: number | string) => `/proposals/${id}`, // PUT - Atualizar proposta
    DELETE: (id: number | string) => `/proposals/${id}`, // DELETE - Deletar proposta
    EXPORT_PDF: (id: number | string) => `/proposals/${id}/export-pdf`, // GET - Download PDF
    EXPORT_DOCX: (id: number | string) => `/proposals/${id}/export-docx`, // GET - Download DOCX
    REGENERATE: (id: number | string) => `/proposals/${id}/regenerate`, // POST - Regenerar proposta
    IMPROVE_ALL: (id: number | string) => `/proposals/${id}/improve-all`, // POST - Melhorar todas as seções com conteúdo
    GENERATION_STATUS: (id: number | string) => `/proposals/${id}/generation-status`, // GET - Status geração/melhoria background
    ELABORATION_INSTRUCTIONS: (id: number | string) => `/proposals/${id}/elaboration-instructions`, // GET - Instruções salvas (?type=generate|improve)
    SETUP_VECTOR_STORE: (id: number | string) => `/proposals/${id}/setup-vector-store`, // POST - Setup vector store
    VECTOR_STORE_STATUS: (id: number | string) => `/proposals/${id}/vector-store-status`, // GET - Status vector store
    EPHEMERAL_DOCUMENT: (id: number | string) => `/proposals/${id}/ephemeral-document`, // POST - Indexa documento efêmero p/ uma geração/melhoria
    IMPORT_DOCUMENT: '/proposals/import-document', // POST - Importar documento como nova proposta
    IMPORT_CONTENT: (id: number | string) => `/proposals/${id}/import-content`, // POST - Importar conteúdo externo e adaptar ao roteiro existente
    VERSIONS: (id: number | string) => `/proposals/${id}/versions`, // GET - lista / POST - cria versão manual
    VERSION_DETAIL: (id: number | string, versionNumber: number) => `/proposals/${id}/versions/${versionNumber}`, // GET - detalhe resolvido / PATCH - renomeia / DELETE - exclui
    VERSION_RESTORE: (id: number | string, versionNumber: number) => `/proposals/${id}/versions/${versionNumber}/restore`, // POST - restaura
    VERSION_FORK: (id: number | string, versionNumber: number) => `/proposals/${id}/versions/${versionNumber}/fork`, // POST - cria proposta nova a partir do snapshot
  },

  // 📚 Groups
  GROUPS: {
    LIST: '/groups/',
    CREATE: '/groups/',
    UPDATE: (id: number) => `/groups/${id}`,
    DELETE: (id: number) => `/groups/${id}`,
  },

  PROPOSAL_TYPES: {
    LIST: '/proposal-types/',
    CREATE: '/proposal-types/',
    DELETE: (id: number) => `/proposal-types/${id}`,
  },

  KNOWLEDGE_BASES: {
    LIST: '/knowledge-bases/',
    CREATE: '/knowledge-bases/',
    UPDATE: (id: number) => `/knowledge-bases/${id}`,
    DELETE: (id: number) => `/knowledge-bases/${id}`,
    ADD_DOCUMENT: (id: number) => `/knowledge-bases/${id}/documents`,
  },

  // 🗺️ Roadmap
  ROADMAP: {
    LIST: '/roadmap/',                                 // GET - Tópicos da proposta (?proposal_id=X)
    DETAILS: (id: number | string) => `/roadmap/${id}`, // GET/PATCH - Detalhes/atualizar um tópico
    GENERATE_CONTENT: '/roadmap/generate-content',    // POST - Gerar conteúdo via IA
    IMPROVE_CONTENT: '/roadmap/improve-content',      // POST - Melhorar conteúdo via IA
    EXTRACT_FROM_DOCUMENT: '/roadmap/extract-from-document', // POST - Extrair roadmap de documento
    GENERATE_FROM_INSTRUCTION: '/roadmap/generate-from-instruction', // POST - Gerar roadmap de instrução
    GENERATE_INSTRUCTION: '/roadmap/generate-instruction', // POST - Gerar instrução individual
    BATCH_GENERATE_INSTRUCTIONS: '/roadmap/batch-generate-instructions', // POST - Gerar múltiplas instruções
  },

  // 📋 Templates
  TEMPLATES: {
    LIST: '/templates/',                               // GET - Listar todos os templates
    CREATE_FROM_PROPOSAL: (id: number | string) => `/templates/from-proposal/${id}`, // POST - Criar template de proposta
    APPLY_TO_PROPOSAL: (templateId: number | string, proposalId: number | string) => `/templates/${templateId}/apply-to/${proposalId}`, // POST - Aplicar template
    DELETE: (id: number | string) => `/templates/${id}`, // DELETE - Deletar template
    UPDATE: (id: number | string) => `/templates/${id}`, // PUT - Atualizar nome/descrição/itens do template
  },

  // 🗺️ Native Roadmap Templates (Roteiros Nativos)
  NATIVE_ROADMAP_TEMPLATES: {
    LIST: '/native-roadmap-templates/',
    DETAILS: (id: number) => `/native-roadmap-templates/${id}`,
    APPLY: (templateId: number, proposalId: number) => `/native-roadmap-templates/${templateId}/apply-to/${proposalId}`,
    // Admin CRUD
    ADMIN_LIST: '/admin/native-roadmap-templates/',
    ADMIN_CREATE: '/admin/native-roadmap-templates/',
    ADMIN_DETAILS: (id: number) => `/admin/native-roadmap-templates/${id}`,
    ADMIN_UPDATE: (id: number) => `/admin/native-roadmap-templates/${id}`,
    ADMIN_DELETE: (id: number) => `/admin/native-roadmap-templates/${id}`,
  },

  // 🧠 Concept Map
  CONCEPT_MAP: {
    GET: (proposalId: number | string) => `/concept-map/${proposalId}`, // GET - Mapa conceitual salvo
  },

  // 👤 Users
  USERS: {
    ME: '/users/me',                                    // GET - Dados do usuário logado
  },

  // 🔐 Permissions
  PERMISSIONS: {
    CHECK_AI_FEATURES: '/permissions/check-ai-features', // GET - Verifica se usuário pode usar IA
  },

  // 🤖 LLM Providers (Multi-LLM Dynamic Abstraction)
  LLM: {
    PROVIDERS: '/api/llm/providers',                // GET - Lista providers disponíveis
  },

  // 💳 Payments
  // Nota: Se /payments/credit-card retornar 404, checkoutService tenta fallback para /subscriptions/
  PAYMENTS: {
    CREDIT_CARD: '/payments/credit-card',              // POST - Pagamento com cartão de crédito (migrado de /api/subscriptions/)
    DEBIT_CARD: '/payments/debit-card',                // POST - Pagamento com cartão de débito
    PIX: '/payments/pix',                              // POST - Pagamento com PIX
    BOLETO: '/payments/boleto',                        // POST - Pagamento com boleto
  },

  // 📋 Plans
  PLANS: {
    LIST: '/plans/',                                    // GET - Listar planos ativos
    ALL: '/plans/all',                                  // GET - Todos os planos (incluindo inativos)
    DETAILS: (id: number | string) => `/plans/${id}`,  // GET - Detalhes do plano por ID
    BY_SLUG: (slug: string) => `/plans/slug/${slug}`,  // GET - Detalhes do plano por slug
    ACTIVE: '/plans/active',                           // GET - Planos ativos (alias de LIST)
  },

  // 💰 Plan Calculations
  PLAN_CALCULATIONS: {
    UPGRADE_COST: (subscriptionId: string | number, planId: string | number) => `/plan-calculations/upgrade-cost/${subscriptionId}/${planId}`,
    DOWNGRADE_COST: (subscriptionId: string | number, planId: string | number) => `/plan-calculations/downgrade-cost/${subscriptionId}/${planId}`,
    CHANGE_COST: (subscriptionId: string | number, planId: string | number) => `/plan-calculations/change-cost/${subscriptionId}/${planId}`,
    RENEWAL_COST: (subscriptionId: string | number) => `/plan-calculations/renewal-cost/${subscriptionId}`,
    PAYMENT_FEE: '/plan-calculations/payment-fee', // POST com query params: amount, payment_method, installments (opcional)
    INSTALLMENT_SIMULATION: '/plan-calculations/installment-simulation', // POST - Simula parcelamento com juros da Asaas
  },

  // 📊 Evaluations (History)
  EVALUATIONS: {
    HISTORY_BY_PROPOSAL: (proposalId: number | string) => `/evaluations/history-by-proposal/${proposalId}`, // GET - Histórico completo
    LATEST_BY_PROPOSAL: (proposalId: number | string) => `/evaluations/latest-by-proposal/${proposalId}`, // GET - Última avaliação
  },

  // 🏦 Organizations (Update/Delete)
  ORGANIZATION_UPDATE: (id: number) => `/organizations/${id}`, // PUT - Atualizar organização

  // 👑 Admin Panel (Master Access Only)
  ADMIN: {
    // Organizations
    ORGANIZATIONS: {
      LIST: '/admin/organizations/',
      DETAILS: (id: number) => `/admin/organizations/${id}`,
      USAGE: (id: number) => `/admin/organizations/${id}/usage`,
      API_USAGE: (id: number) => `/admin/organizations/${id}/api-usage`,
      USERS: (id: number) => `/admin/organizations/${id}/users`,
      SUBSCRIPTIONS: (id: number) => `/admin/organizations/${id}/subscriptions`,
      SUSPEND: (id: number) => `/admin/organizations/${id}/suspend`,
      ACTIVATE: (id: number) => `/admin/organizations/${id}/activate`,
    },
    // Users
    USERS: {
      LIST: '/admin/users/',
      DETAILS: (id: number) => `/admin/users/${id}`,
      DEACTIVATE: (id: number) => `/admin/users/${id}/deactivate`,
      ACTIVATE: (id: number) => `/admin/users/${id}/activate`,
    },
    // Subscriptions
    SUBSCRIPTIONS: {
      LIST: '/admin/subscriptions/',
      DETAILS: (id: number) => `/admin/subscriptions/${id}`,
    },
    // Plans
    PLANS: {
      LIST: '/admin/plans/',
      DETAILS: (id: number) => `/admin/plans/${id}`,
      CREATE: '/admin/plans/',
      UPDATE: (id: number) => `/admin/plans/${id}`,
    },
    // Coupons
    COUPONS: {
      LIST: '/admin/coupons/',
      DETAILS: (id: number) => `/admin/coupons/${id}`,
      CREATE: '/admin/coupons/',
      UPDATE: (id: number) => `/admin/coupons/${id}`,
      DELETE: (id: number) => `/admin/coupons/${id}`,
    },
    // Analytics & Reports
    STATS: '/admin/stats',
    REPORTS: {
      REVENUE: '/admin/reports/revenue',
      USER_ACTIVITY: '/admin/reports/user-activity',
    },
  },
} as const;

/**
 * Exemplo de uso:
 * 
 * await api.get(API_ENDPOINTS.USERS.ME);
 * await api.get(API_ENDPOINTS.PROPOSALS.LIST);
 * await api.get(API_ENDPOINTS.PROPOSALS.DETAILS(123));
 * await api.post(API_ENDPOINTS.PAYMENTS.CREDIT_CARD, payload);
 */
